import {
  ExtraInfo,
  OnboardingDetailedStep,
  OnboardingStep,
  TradingMethodType,
} from "@shared/features/onboarding/types";
import { EXCHANGES, PRIMARY_EXCHANGES } from "@shared/common/constants";
import { TranslationKeys } from "@shared/common/i18n/types";
import { LanguageNameSpaces } from "@shared/types";

export const ONBOARDING_STEP_KEY = "onboarding_step";
export const CONNECT_TELEGRAM_KEY = "connect_telegram";

export const STEP_TO_TITLE: {
  [step in OnboardingStep]: TranslationKeys<LanguageNameSpaces.ONBOARDING>;
} = {
  [OnboardingStep.CONNECT_EXCHANGE]: "step_titles.connect_exchange",
  [OnboardingStep.TRADING_METHOD]: "step_titles.trading_method",
  [OnboardingStep.METHOD_SETTINGS]: "step_titles.method_settings",
  [OnboardingStep.OVERVIEW]: "step_titles.overview",
};

export const PREVIOUS_STEP_TO_BACK_BUTTON_TITLE: {
  [step in OnboardingDetailedStep]?: TranslationKeys<LanguageNameSpaces.ONBOARDING>;
} = {
  [OnboardingDetailedStep.SELECT_EXCHANGE]:
    "back_button_titles.select_exchange",
  [OnboardingDetailedStep.COPY_DCA_BOT]: "back_button_titles.copy_dca_bot",
  [OnboardingDetailedStep.FOLLOW_SIGNALS_GROUP]:
    "back_button_titles.follow_signals_group",
  [OnboardingDetailedStep.TRADING_METHOD]: "back_button_titles.trading_method",
};

export const SKIP_ONBOARDING_TRADING_METHOD = "skip_messages.trading_method";
export const SKIP_ONBOARDING_FOLLOW_GROUP = "skip_messages.follow_group";
export const MOBILE_SKIP_ONBOARDING_FOLLOW_GROUP_HAS_GROUPS =
  "skip_messages.mobile_follow_group_has_groups";
export const SKIP_ONBOARDING_SIGNALS_BOT_HAS_GROUPS =
  "skip_messages.signals_bot_has_groups";
export const SKIP_ONBOARDING_DCA_BOT = "skip_messages.dca_bot";

export const DEMO_MODE_CONNECT_EXCHANGE_PRIMARY_TITLE =
  "connect_exchange.demo_mode_title";

export const API_KEYS_ARTICLE = "5814843";

export const TRADING_METHOD_TO_FULL_LIST: Record<
  TradingMethodType,
  TranslationKeys<LanguageNameSpaces.ONBOARDING>
> = {
  [TradingMethodType.DCA_BOT]: "trading_method.full_list.dca_bot",
  [TradingMethodType.SIGNALS_GROUP]: "trading_method.full_list.signals_group",
};

export const ONBOARDING_GROUP_CARD_WIDTH = 432;
export const MOBILE_ONBOARDING_GROUP_CARD_WIDTH = 330;
export const ONBOARDING_GROUP_CARD_HEIGHT = 234;
export const MOBILE_ONBOARDING_GROUP_CARD_HEIGHT = 250;
export const ONBOARDING_MY_GROUP_CARD_HEIGHT = 154;

export const EXTERNAL_ACCOUNT_WHY_CONNECT_TITLE =
  "external_account.why_connect_title";
export const EXTERNAL_ACCOUNT_WHY_CONNECT_DESCRIPTION =
  "external_account.why_connect_description";
export const WHAT_IS_UUID_TITLE = "external_account.uuid_title";
export const WHAT_IS_UUID_DESCRIPTION = "external_account.uuid_description";
export const UUID_ALREADY_CONNECTED_WARNING_MESSAGE =
  "external_account.uuid_already_connected";

export const BOT_CARD_WIDTH = 432;
export const MOBILE_BOT_CARD_WIDTH = 330;
export const BOT_CARD_HEIGHT = 306;
export const MOBILE_BOT_CARD_HEIGHT = 274;

export const RADIO_SIZE = 16;
export const EXCHANGE_ICON_SIZE = 16;
export const EXCHANGE_ICON_MARGIN = 6;

export const MIN_USD_AMOUNT_PER_TRADE = 10;
export const MIN_BINANCE_USD_AMOUNT_PER_TRADE = 30;
export const MAX_GROUP_CONFIGURATIONS = 3;
export const MAX_ACCOUNTS_ROWS = 3;

export const TRADING_METHOD_TO_SKIP_CREATE_BOT: Record<
  TradingMethodType,
  TranslationKeys<LanguageNameSpaces.ONBOARDING>
> = {
  [TradingMethodType.DCA_BOT]: "skip_messages.skip_dca_bot",
  [TradingMethodType.SIGNALS_GROUP]: "skip_messages.skip_signals_bot",
};

export const BOT_AMOUNT_PER_TRADE_TOOLTIP = "tooltips.bot_amount_per_trade";

export const UNCHECKED_CHECKBOX_MESSAGE = "messages.unchecked_checkbox";

export const CONFIRM_AUTO_TRADING_MESSAGE = "messages.confirm_auto_trading";

export const FALLBACK_MAIN_COINS = ["USDT", "USD", "BTC", "BUSD", "USDC"];

export const EXCHANGE_TO_CREATE_API_KEYS_PERMISSIONS: Record<
  string,
  TranslationKeys<LanguageNameSpaces.PORTFOLIO>
> = {
  [EXCHANGES.BINANCE]: "api_keys_guide.permissions.spot_margin",
  [EXCHANGES.BINANCE_FUTURES]: "api_keys_guide.permissions.futures",
  [EXCHANGES.BINANCE_COIN_FUTURES]: "api_keys_guide.permissions.futures",
  [EXCHANGES.BYBIT_SPOT]: "api_keys_guide.permissions.trade_under_spot",
  [EXCHANGES.BYBIT_SPOT_MARGIN]: "api_keys_guide.permissions.trade_under_spot",
  [EXCHANGES.BYBIT_USDT]:
    "api_keys_guide.permissions.orders_positions_contracts",
  [EXCHANGES.BYBIT_INVERSE]:
    "api_keys_guide.permissions.orders_positions_contracts",
  [EXCHANGES.KUCOIN_FUTURES]: "api_keys_guide.permissions.future_trading",
  [EXCHANGES.BITGET_FUTURES]:
    "api_keys_guide.permissions.orders_holdings_futures",
  [EXCHANGES.BINGX_SPOT]: "api_keys_guide.permissions.spot_trading",
  [EXCHANGES.BINGX_FUTURES]:
    "api_keys_guide.permissions.perpetual_futures_trading",
};

export const BINANCE_CREATE_API_KEYS_GUIDE: TranslationKeys<LanguageNameSpaces.PORTFOLIO>[] =
  [
    "api_keys_guide.binance.step_1",
    "api_keys_guide.binance.step_2",
    "api_keys_guide.binance.step_3",
    "api_keys_guide.binance.step_4",
    "api_keys_guide.binance.step_5",
    "api_keys_guide.binance.step_6",
    "api_keys_guide.binance.step_7",
    "api_keys_guide.binance.step_8",
    "api_keys_guide.binance.step_9",
    "api_keys_guide.binance.step_10",
  ];
export const BYBIT_SPOT_CREATE_API_KEYS_GUIDE: TranslationKeys<LanguageNameSpaces.PORTFOLIO>[] =
  [
    "api_keys_guide.bybit_spot.step_1",
    "api_keys_guide.bybit_spot.step_2",
    "api_keys_guide.bybit_spot.step_3",
    "api_keys_guide.bybit_spot.step_4",
    "api_keys_guide.bybit_spot.step_5",
    "api_keys_guide.bybit_spot.step_6",
    "api_keys_guide.bybit_spot.step_7",
    "api_keys_guide.bybit_spot.step_8",
    "api_keys_guide.bybit_spot.step_9",
  ];
export const KUCOIN_CREATE_API_KEYS_GUIDE: TranslationKeys<LanguageNameSpaces.PORTFOLIO>[] =
  [
    "api_keys_guide.kucoin.step_1",
    "api_keys_guide.kucoin.step_2",
    "api_keys_guide.kucoin.step_3",
    "api_keys_guide.kucoin.step_4",
    "api_keys_guide.kucoin.step_5",
    "api_keys_guide.kucoin.step_6",
    "api_keys_guide.kucoin.step_7",
  ];
export const OKX_CREATE_API_KEYS_GUIDE: TranslationKeys<LanguageNameSpaces.PORTFOLIO>[] =
  [
    "api_keys_guide.okx.step_1",
    "api_keys_guide.okx.step_2",
    "api_keys_guide.okx.step_3",
    "api_keys_guide.okx.step_4",
    "api_keys_guide.okx.step_5",
    "api_keys_guide.okx.step_6",
    "api_keys_guide.okx.step_7",
  ];
export const BITMEX_CREATE_API_KEYS_GUIDE: TranslationKeys<LanguageNameSpaces.PORTFOLIO>[] =
  [
    "api_keys_guide.bitmex.step_1",
    "api_keys_guide.bitmex.step_2",
    "api_keys_guide.bitmex.step_3",
    "api_keys_guide.bitmex.step_4",
    "api_keys_guide.bitmex.step_5",
    "api_keys_guide.bitmex.step_6",
    "api_keys_guide.bitmex.step_7",
  ];
export const HUOBI_CREATE_API_KEYS_GUIDE: TranslationKeys<LanguageNameSpaces.PORTFOLIO>[] =
  [
    "api_keys_guide.huobi.step_1",
    "api_keys_guide.huobi.step_2",
    "api_keys_guide.huobi.step_3",
    "api_keys_guide.huobi.step_4",
    "api_keys_guide.huobi.step_5",
    "api_keys_guide.huobi.step_6",
    "api_keys_guide.huobi.step_7",
    "api_keys_guide.huobi.step_8",
    "api_keys_guide.huobi.step_9",
  ];
export const BITGET_CREATE_API_KEYS_GUIDE: TranslationKeys<LanguageNameSpaces.PORTFOLIO>[] =
  [
    "api_keys_guide.bitget.step_1",
    "api_keys_guide.bitget.step_2",
    "api_keys_guide.bitget.step_3",
    "api_keys_guide.bitget.step_4",
    "api_keys_guide.bitget.step_5",
    "api_keys_guide.bitget.step_6",
    "api_keys_guide.bitget.step_7",
    "api_keys_guide.bitget.step_8",
    "api_keys_guide.bitget.step_9",
    "api_keys_guide.bitget.step_10",
    "api_keys_guide.bitget.step_11",
    "api_keys_guide.bitget.step_12",
    "api_keys_guide.bitget.step_13",
  ];
export const COINBASE_CREATE_API_KEYS_GUIDE: TranslationKeys<LanguageNameSpaces.PORTFOLIO>[] =
  [
    "api_keys_guide.coinbase.step_1",
    "api_keys_guide.coinbase.step_2",
    "api_keys_guide.coinbase.step_3",
    "api_keys_guide.coinbase.step_4",
    "api_keys_guide.coinbase.step_5",
    "api_keys_guide.coinbase.step_6",
    "api_keys_guide.coinbase.step_7",
  ];
export const HYPERLIQUID_CREATE_API_KEYS_GUIDE: TranslationKeys<LanguageNameSpaces.PORTFOLIO>[] =
  [
    "api_keys_guide.hyperliquid.step_1",
    "api_keys_guide.hyperliquid.step_2",
    "api_keys_guide.hyperliquid.step_3",
    "api_keys_guide.hyperliquid.step_4",
    "api_keys_guide.hyperliquid.step_5",
    "api_keys_guide.hyperliquid.step_6",
  ];
export const BINGX_CREATE_API_KEYS_GUIDE: TranslationKeys<LanguageNameSpaces.PORTFOLIO>[] =
  [
    "api_keys_guide.bingx.step_1",
    "api_keys_guide.bingx.step_2",
    "api_keys_guide.bingx.step_3",
    "api_keys_guide.bingx.step_4",
    "api_keys_guide.bingx.step_5",
    "api_keys_guide.bingx.step_6",
    "api_keys_guide.bingx.step_7",
    "api_keys_guide.bingx.step_8",
    "api_keys_guide.bingx.step_9",
  ];
export const BLOFIN_CREATE_API_KEYS_GUIDE: TranslationKeys<LanguageNameSpaces.PORTFOLIO>[] =
  [
    "api_keys_guide.blofin.step_1",
    "api_keys_guide.blofin.step_2",
    "api_keys_guide.blofin.step_3",
    "api_keys_guide.blofin.step_4",
    "api_keys_guide.blofin.step_5",
    "api_keys_guide.blofin.step_6",
    "api_keys_guide.blofin.step_7",
  ];
export const MEXC_CREATE_API_KEYS_GUIDE: TranslationKeys<LanguageNameSpaces.PORTFOLIO>[] =
  [
    "api_keys_guide.mexc.step_1",
    "api_keys_guide.mexc.step_2",
    "api_keys_guide.mexc.step_3",
    "api_keys_guide.mexc.step_4",
    "api_keys_guide.mexc.step_5",
    "api_keys_guide.mexc.step_6",
    "api_keys_guide.mexc.step_7",
    "api_keys_guide.mexc.step_8",
    "api_keys_guide.mexc.step_9",
    "api_keys_guide.mexc.step_10",
    "api_keys_guide.mexc.step_11",
  ];
export const KRAKEN_CREATE_API_KEYS_GUIDE: TranslationKeys<LanguageNameSpaces.PORTFOLIO>[] =
  [
    "api_keys_guide.kraken.step_1",
    "api_keys_guide.kraken.step_2",
    "api_keys_guide.kraken.step_3",
    "api_keys_guide.kraken.step_4",
    "api_keys_guide.kraken.step_5",
    "api_keys_guide.kraken.step_6",
    "api_keys_guide.kraken.step_7",
    "api_keys_guide.kraken.step_8",
  ];

export const PRIMARY_EXCHANGE_TO_CREATE_API_KEYS_GUIDE: Record<
  string,
  TranslationKeys<LanguageNameSpaces.PORTFOLIO>[]
> = {
  [PRIMARY_EXCHANGES.BINANCE]: BINANCE_CREATE_API_KEYS_GUIDE,
  [PRIMARY_EXCHANGES.BYBIT]: BYBIT_SPOT_CREATE_API_KEYS_GUIDE,
  [PRIMARY_EXCHANGES.KUCOIN]: KUCOIN_CREATE_API_KEYS_GUIDE,
  [PRIMARY_EXCHANGES.OKX]: OKX_CREATE_API_KEYS_GUIDE,
  [PRIMARY_EXCHANGES.OKX_EU]: OKX_CREATE_API_KEYS_GUIDE,
  [PRIMARY_EXCHANGES.BITMEX]: BITMEX_CREATE_API_KEYS_GUIDE,
  [PRIMARY_EXCHANGES.HUOBI]: HUOBI_CREATE_API_KEYS_GUIDE,
  [PRIMARY_EXCHANGES.BITGET]: BITGET_CREATE_API_KEYS_GUIDE,
  [PRIMARY_EXCHANGES.COINBASE]: COINBASE_CREATE_API_KEYS_GUIDE,
  [PRIMARY_EXCHANGES.HYPERLIQUID]: HYPERLIQUID_CREATE_API_KEYS_GUIDE,
  [PRIMARY_EXCHANGES.BINGX]: BINGX_CREATE_API_KEYS_GUIDE,
  [PRIMARY_EXCHANGES.BLOFIN]: BLOFIN_CREATE_API_KEYS_GUIDE,
  [PRIMARY_EXCHANGES.MEXC]: MEXC_CREATE_API_KEYS_GUIDE,
  [PRIMARY_EXCHANGES.KRAKEN]: KRAKEN_CREATE_API_KEYS_GUIDE,
};

export const EXTRA_INFO_TO_TITLE: Record<
  ExtraInfo,
  TranslationKeys<LanguageNameSpaces.ONBOARDING>
> = {
  [ExtraInfo.WHY_NEED]: EXTERNAL_ACCOUNT_WHY_CONNECT_TITLE,
  [ExtraInfo.UUID]: WHAT_IS_UUID_TITLE,
};

export const EXTRA_INFO_TO_DESCRIPTION: Record<
  ExtraInfo,
  TranslationKeys<LanguageNameSpaces.ONBOARDING>
> = {
  [ExtraInfo.WHY_NEED]: EXTERNAL_ACCOUNT_WHY_CONNECT_DESCRIPTION,
  [ExtraInfo.UUID]: WHAT_IS_UUID_DESCRIPTION,
};

export const CONNECT_TELEGRAM_INFO = "messages.connect_telegram_info";

export const BINANCE_API_KEYS_PATH =
  "https://www.binance.com/en/my/settings/api-management";

export const USD_PRECISION = 2;

export const QUICK_CONNECT_API_EXISTS_ERROR_CODE = 409;
export const QUICK_CONNECT_API_TERMS_ERROR_CODE = 400;
// Binance's 823013, answered by the server's BINANCE_MAIN_ACCOUNT_REQUIRED_ERROR.
export const QUICK_CONNECT_API_TERMS_ERROR_PATTERN = /accept the API terms/i;
export const CREATE_ACCOUNT_ERROR_DEFAULT_TITLE =
  "errors.create_account_default_title";

export const CONNECT_DEMO_ACCOUNT_EXTRA_INFO_ARTICLE_ID = "9853159";
